// WP-1.8 (S-15 `/owed`, M11 RP-1..4): two tabs (Receivables/Payables), rows with remaining
// + due + overdue badge, Settle (records a real transaction and links it — RP-2), Write-off
// / Waive (ConfirmDialog types the amount — RP-3), Reopen. "Settle" always creates the
// paying transaction fresh rather than offering a picker over existing unlinked candidates
// (§18.5's other option) — that needs a new "which transactions are unlinked" query this WP
// doesn't build; surfaced as a scope note, not resolved.
import {
  CURRENCIES,
  formatMoney,
  fromMinor,
  parseAmountInput,
  type CurrencyCode,
  type RpKind,
} from '@pfos/engine';
import type { RpResponse } from '@pfos/shared';
import { HandCoins, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AccountPicker } from '../components/AccountPicker';
import { Amount } from '../components/Amount';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { PartyPicker } from '../components/PartyPicker';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { ApiError } from '../lib/api';
import { useParties } from '../lib/parties';
import { useCloseRp, useCreateRp, useReopenRp, useRps, useSettleRp } from '../lib/rps';
import { useRecordTransaction } from '../lib/transactions';

function SettleRow({
  rp,
  remaining,
  onDone,
}: {
  rp: RpResponse;
  remaining: number;
  onDone: () => void;
}): React.JSX.Element {
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recordMutation = useRecordTransaction();
  const settleMutation = useSettleRp(rp.id);

  return (
    <div className="mt-2 rounded-[var(--radius-control)] border border-border p-3">
      <p className="mb-2 text-xs text-muted">
        Records a real {rp.kind === 'receivable' ? 'income' : 'expense'} transaction and links it as
        the settlement (RP-2).
      </p>
      <div className="mb-2 flex gap-2">
        <div className="flex-1">
          <Field label="Account" htmlFor={`settle-account-${rp.id}`}>
            <AccountPicker
              id={`settle-account-${rp.id}`}
              value={accountId}
              onChange={setAccountId}
              currencyFilter={rp.currency}
            />
          </Field>
        </div>
        <div className="flex-1">
          <AmountField
            id={`settle-amount-${rp.id}`}
            ariaLabel="Settlement amount"
            currency={rp.currency}
            value={amount}
            onChange={setAmount}
          />
        </div>
      </div>
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={recordMutation.isPending || settleMutation.isPending}
        onClick={() => {
          setError(null);
          if (accountId === '') {
            setError('Choose an account');
            return;
          }
          const parsed = parseAmountInput(amount, { defaultCurrency: rp.currency });
          if (!parsed.ok) return; // AmountField already shows this inline
          if (parsed.money.minorUnits > remaining) {
            setError('Amount exceeds the remaining balance');
            return;
          }
          recordMutation.mutate(
            {
              type: rp.kind === 'receivable' ? 'income' : 'expense',
              accountId,
              amountMinor: parsed.money.minorUnits,
              note: `Settles ${rp.kind} — ${rp.party}`,
            },
            {
              onSuccess: (tx) => {
                settleMutation.mutate(
                  { amountMinor: parsed.money.minorUnits, transactionId: tx.id },
                  {
                    onSuccess: onDone,
                    onError: (e) => {
                      setError(e instanceof ApiError ? e.message : 'Could not link the settlement');
                    },
                  },
                );
              },
              onError: (e) => {
                setError(e instanceof ApiError ? e.message : 'Could not record the transaction');
              },
            },
          );
        }}
      >
        Record & settle
      </button>
      {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
}

function CloseAction({ rp }: { rp: RpResponse }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeMutation = useCloseRp(rp.id);
  const verb = rp.kind === 'receivable' ? 'Write off' : 'Waive';

  return (
    <>
      <button
        type="button"
        className={ghostButtonInlineClass}
        onClick={() => {
          setOpen(true);
        }}
      >
        {verb}
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`${verb} ${rp.kind}`}
        consequence={`This closes ${rp.party}'s ${rp.kind} without any cash movement. It can be reopened later (RP-3).`}
        confirmLabel={verb}
        requireReason
        requireTypedConfirmation={formatMoney(fromMinor(rp.currency, rp.amountMinor))}
        confirming={closeMutation.isPending}
        onConfirm={(reason) => {
          setError(null);
          closeMutation.mutate(reason, {
            onSuccess: () => {
              setOpen(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not close');
            },
          });
        }}
      />
      {error !== null && <p className="mt-1 text-sm text-danger">{error}</p>}
    </>
  );
}

function RpRow({ rp }: { rp: RpResponse }): React.JSX.Element {
  const [settling, setSettling] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const reopenMutation = useReopenRp(rp.id);
  const remaining = rp.amountMinor - rp.settledMinor;
  const overdue =
    rp.status === 'open' && rp.dueDate !== undefined && Date.parse(rp.dueDate) < Date.now();

  return (
    <li className="border-b border-border py-3 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-ink">{rp.party}</p>
          <p className="text-xs text-muted">{rp.reason}</p>
        </div>
        <div className="text-right">
          <Amount currency={rp.currency} minorUnits={remaining} className="font-medium" />
          <p className="text-xs text-muted">
            of <Amount currency={rp.currency} minorUnits={rp.amountMinor} />
          </p>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
        {rp.dueDate !== undefined && <span>Due {rp.dueDate.slice(0, 10)}</span>}
        {overdue && (
          <span className="rounded-full bg-danger/10 px-2 py-0.5 font-medium text-danger">
            Overdue
          </span>
        )}
        <span className="capitalize">{rp.status.replace('_', ' ')}</span>
      </div>

      {rp.status === 'open' && (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setSettling((v) => !v);
            }}
          >
            Settle
          </button>
          <CloseAction rp={rp} />
        </div>
      )}
      {rp.status === 'closed' && (
        <div className="mt-2">
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={reopenMutation.isPending}
            onClick={() => {
              reopenMutation.mutate();
            }}
          >
            Reopen
          </button>
        </div>
      )}

      {rp.settlements.length > 0 && (
        <button
          type="button"
          className="mt-2 text-xs text-primary hover:underline"
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'Hide' : 'Show'} settlements ({rp.settlements.length})
        </button>
      )}
      {expanded && (
        <ul className="mt-2 space-y-1 text-xs text-muted">
          {rp.settlements.map((s, i) => (
            <li key={`${s.transactionId}-${String(i)}`}>
              <Amount currency={rp.currency} minorUnits={s.amountMinor} /> on {s.at.slice(0, 10)}
            </li>
          ))}
        </ul>
      )}

      {settling && (
        <SettleRow
          rp={rp}
          remaining={remaining}
          onDone={() => {
            setSettling(false);
          }}
        />
      )}
    </li>
  );
}

function CreateRpForm({
  open,
  onOpenChange,
  kind,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: RpKind;
}): React.JSX.Element {
  const [party, setParty] = useState('');
  const [partyId, setPartyId] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateRp();
  const { data: parties } = useParties(false);

  useEffect(() => {
    if (open) {
      setParty('');
      setPartyId('');
      setCurrency('PKR');
      setAmount('');
      setReason('');
      setDueDate('');
      setNotes('');
      setError(null);
    }
  }, [open]);

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={kind === 'receivable' ? 'New receivable' : 'New payable'}
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (party.trim() === '') {
          setError('Party is required');
          return;
        }
        if (reason.trim() === '') {
          setError('Reason is required');
          return;
        }
        const parsed = parseAmountInput(amount, { defaultCurrency: currency });
        if (!parsed.ok) return; // AmountField already shows this inline
        createMutation.mutate(
          {
            kind,
            party: party.trim(),
            ...(partyId !== '' ? { partyId } : {}),
            amountMinor: parsed.money.minorUnits,
            currency,
            reason: reason.trim(),
            ...(dueDate !== '' ? { dueDate: new Date(dueDate).toISOString() } : {}),
            ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
          },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      <Field label="Party (optional link)" htmlFor="rp-party-id">
        <PartyPicker
          id="rp-party-id"
          value={partyId}
          onChange={(id) => {
            setPartyId(id);
            const matched = (parties ?? []).find((p) => p.id === id);
            if (matched !== undefined) setParty(matched.name);
          }}
        />
      </Field>
      <Field label="Party name" htmlFor="rp-party">
        <input
          id="rp-party"
          className={inputClass}
          value={party}
          onChange={(e) => {
            setParty(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency" htmlFor="rp-currency">
        <select
          id="rp-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {Object.keys(CURRENCIES).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Amount" htmlFor="rp-amount">
        <AmountField id="rp-amount" currency={currency} value={amount} onChange={setAmount} />
      </Field>
      <Field label="Reason" htmlFor="rp-reason">
        <input
          id="rp-reason"
          className={inputClass}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </Field>
      <Field label="Due date (optional)" htmlFor="rp-due">
        <input
          id="rp-due"
          type="date"
          className={inputClass}
          value={dueDate}
          onChange={(e) => {
            setDueDate(e.target.value);
          }}
        />
      </Field>
      <Field label="Notes (optional)" htmlFor="rp-notes">
        <input
          id="rp-notes"
          className={inputClass}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
        />
      </Field>
      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}

export function OwedPage(): React.JSX.Element {
  const [kind, setKind] = useState<RpKind>('receivable');
  const { data: rps, isLoading } = useRps({ kind });
  const [formOpen, setFormOpen] = useState(false);
  const label = kind === 'receivable' ? 'Receivables' : 'Payables';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Owed</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New
        </button>
      </div>

      <div className="mb-4 flex gap-2" role="radiogroup" aria-label="Owed kind">
        {(['receivable', 'payable'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            onClick={() => {
              setKind(k);
            }}
            className={`rounded-[var(--radius-control)] border px-3 py-1.5 text-sm ${
              kind === k
                ? 'border-primary bg-primary/10 font-medium text-primary'
                : 'border-border text-ink hover:bg-bg'
            }`}
          >
            {k === 'receivable' ? 'Receivables' : 'Payables'}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (rps === undefined || rps.length === 0) && (
        <EmptyState
          icon={HandCoins}
          message={`No ${label.toLowerCase()} yet.`}
          action={{
            label: 'New',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {rps !== undefined && rps.length > 0 && (
        <ul className="rounded-[var(--radius-card)] border border-border bg-surface px-4">
          {rps.map((rp) => (
            <RpRow key={rp.id} rp={rp} />
          ))}
        </ul>
      )}

      <CreateRpForm open={formOpen} onOpenChange={setFormOpen} kind={kind} />
    </div>
  );
}
